import { useState, useMemo } from 'react';
import { useReportData } from '../hooks/useReportData';
import { ReportPieChart } from '../components/report/ReportPieChart';
import { ReportLineChart } from '../components/report/ReportLineChart';
import { ReportDrillDown } from '../components/report/ReportDrillDown';
import { ReportSection } from '../components/report/ReportSection';
import { KpiCard } from '../components/KpiCard';
import { fmtEur, fmtEur2, fmtJours, fmtNum } from '../components/report/formatters';
import type { ReportParams } from '../types/widget';

interface PieRow { tranche: string; tri: number; nb_factures: number; montant_ht: number }
interface KpiRow {
    nb_factures: number; delai_moyen_jours: number; delai_min: number; delai_max: number;
    montant_moyen_ht: number; nb_factures_inconnu: number; montant_inconnu_ht: number;
}
interface CourbeRow { mois: string; client_code?: string; client_label?: string; nb_factures: number; delai_moyen_jours: number; nb_factures_inconnu: number }

export function DelaisReglement({ filters, variant }: { filters: ReportParams; variant: 'client' | 'prestataire' }) {
    const [drillFilter, setDrillFilter] = useState<{ tranche?: string; mois?: string } | null>(null);

    const base = `/api/emis/delais-reglement/${variant}`;
    const pie = useReportData<PieRow>(`${base}/pie`, filters);
    const kpi = useReportData<KpiRow>(`${base}/kpi`, filters);
    const courbe = useReportData<CourbeRow>(`${base}/courbe`, filters);

    const pieNb = useMemo(
        () => pie.data.map((r) => ({ name: r.tranche, value: Number(r.nb_factures), sort_order: r.tri })),
        [pie.data],
    );
    const pieMontant = useMemo(
        () => pie.data.map((r) => ({ name: r.tranche, value: Number(r.montant_ht), sort_order: r.tri })),
        [pie.data],
    );

    const kpiRow = kpi.data[0];

    const courbeData = useMemo(() => {
        if (variant === 'prestataire') {
            const months = [...new Set(courbe.data.map((r) => r.mois))].sort();
            const byMonth = new Map(courbe.data.map((r) => [r.mois, r]));
            return {
                months,
                series: [{
                    name: 'Délai moyen (jours)',
                    data: months.map((m) => byMonth.get(m)?.delai_moyen_jours ?? null),
                }],
            };
        }
        const months = [...new Set(courbe.data.map((r) => r.mois))].sort();
        const entities = [...new Set(courbe.data.map((r) => r.client_code!))];
        const byKey = new Map(courbe.data.map((r) => [`${r.client_code}-${r.mois}`, r]));
        const series = entities.map((code) => ({
            name: courbe.data.find((r) => r.client_code === code)?.client_label ?? code,
            data: months.map((m) => byKey.get(`${code}-${m}`)?.delai_moyen_jours ?? null),
            lineStyle: (code === 'TOUS' ? 'dashed' : 'solid') as 'dashed' | 'solid',
        }));
        return { months, series };
    }, [courbe.data, variant]);

    const drillParams = useMemo(
        () => ({ ...filters, ...drillFilter }),
        [filters, drillFilter],
    );

    const drillColumns = variant === 'prestataire'
        ? [
              { key: 'fournisseur', label: 'Fournisseur', filterable: true },
              { key: 'numero_facture', label: 'N° facture', filterable: true },
              { key: 'libelle', label: 'Libellé', filterable: true },
              { key: 'date_facture', label: 'Date facture' },
              { key: 'date_echeance', label: 'Échéance' },
              { key: 'montant_ht', label: 'Montant HT', align: 'right' as const, format: (v: unknown) => fmtEur2(Number(v)), totalKey: 'montant_ht', formatTotal: (v: number) => fmtEur2(v) },
              { key: 'date_paiement', label: 'Date paiement' },
              { key: 'delai_jours', label: 'Délai (j)', align: 'right' as const },
              { key: 'tranche', label: 'Tranche', filterable: true },
          ]
        : [
              { key: 'pennylane_customer_name', label: 'Client (tiers)', filterable: true, format: (v: unknown) => v ? String(v) : '—' },
              { key: 'client_code', label: 'Client (catégorie)', filterable: true },
              { key: 'client_label', label: 'Nom client', filterable: true },
              { key: 'numero_facture', label: 'N° facture', filterable: true },
              { key: 'libelle', label: 'Libellé', filterable: true },
              { key: 'date_facture', label: 'Date facture' },
              { key: 'montant_ht', label: 'Montant HT', align: 'right' as const, format: (v: unknown) => fmtEur2(Number(v)), totalKey: 'montant_ht', formatTotal: (v: number) => fmtEur2(v) },
              { key: 'date_paiement', label: 'Date paiement' },
              { key: 'delai_jours', label: 'Délai (j)', align: 'right' as const },
              { key: 'tranche', label: 'Tranche', filterable: true },
          ];

    // Tranches ordonnées par tri
    const orderedTranches = useMemo(
        () => [...pie.data].sort((a, b) => a.tri - b.tri).map((r) => r.tranche),
        [pie.data],
    );

    const handleTrancheChange = (tranche: string) => {
        setDrillFilter({ tranche });
    };

    return (
        <div className="space-y-6">
            {/* KPI Strip — en haut */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard label="Délai moyen" value={kpiRow?.delai_moyen_jours ?? '—'} unit="jours" loading={kpi.loading} />
                <KpiCard label="Délai min" value={kpiRow?.delai_min ?? '—'} unit="jours" loading={kpi.loading} />
                <KpiCard label="Délai max" value={kpiRow?.delai_max ?? '—'} unit="jours" loading={kpi.loading} />
                <KpiCard label="Factures connues" value={kpiRow ? fmtNum(Number(kpiRow.nb_factures)) : '—'} loading={kpi.loading} />
            </div>

            {/* Pies côte à côte */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <ReportSection title="Répartition par nombre" loading={pie.loading} error={pie.error}>
                    <ReportPieChart
                        title="Nombre de factures par tranche"
                        data={pieNb}
                        height={300}
                        onSliceClick={(name) => setDrillFilter({ tranche: name })}
                    />
                </ReportSection>
                <ReportSection title="Répartition par montant" loading={pie.loading} error={pie.error}>
                    <ReportPieChart
                        title="Montant HT par tranche"
                        data={pieMontant}
                        height={300}
                        valueFormatter={fmtEur}
                        onSliceClick={(name) => setDrillFilter({ tranche: name })}
                    />
                </ReportSection>
            </div>

            {drillFilter?.tranche && (
                <ReportDrillDown
                    title={`Factures réglées — ${drillFilter.tranche}`}
                    endpoint={`${base}/drill-down`}
                    params={drillParams}
                    enabled={true}
                    onClose={() => setDrillFilter(null)}
                    columns={drillColumns}
                    tranches={orderedTranches}
                    currentTranche={drillFilter.tranche}
                    onTrancheChange={handleTrancheChange}
                />
            )}

            {/* Courbe */}
            <ReportSection title="Évolution mensuelle" loading={courbe.loading} error={courbe.error}>
                <ReportLineChart
                    title={`Délai moyen de règlement — ${variant === 'prestataire' ? 'Prestataires' : 'Clients'}`}
                    categories={courbeData.months}
                    series={courbeData.series}
                    valueFormatter={fmtJours}
                    onPointClick={(_, mois) => setDrillFilter({ mois })}
                />
                {drillFilter?.mois && (
                    <ReportDrillDown
                        title={`Factures réglées — ${drillFilter.mois}`}
                        endpoint={`${base}/drill-down`}
                        params={drillParams}
                        enabled={true}
                        onClose={() => setDrillFilter(null)}
                        columns={drillColumns}
                    />
                )}
            </ReportSection>
        </div>
    );
}
